import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Switch,
  Divider,
  FormControlLabel,
  IconButton,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  useTSQLot,
  useTSMCreateLot,
  useTSMUpdateLot,
} from "../../hooks/useTSLots";
import { useTSQItemList } from "../../hooks/useTSItems";
import { useTSQUOMUnitList } from "../../hooks/useTSUnits";
import { useTSQLocationList } from "../../hooks/useTSLocations";
import { useTSQInvStatusList } from "../../hooks/useTSInvStatus";
import {
  TypedSelectOption,
  UnitOption,
  ItemOption,
  EmployeeOption,
  LotOption,
  LocationOption,
  InventoryStatusOption,
  VendorOption,
} from "../../types/common/selectOptions";
import { useAuth } from "../../contexts/AuthContextWithQuery";
import { DisplayField } from "../../components/common/CustomFields";
import { useTSQPeopleList } from "../../hooks/useTSEntities";
import { useTSQWorkOrderListView } from "../../hooks/useTSWorkOrder";
import { useTSQPurchOrderList } from "../../hooks/useTSPurchaseOrder";
import { useTSQLotList } from "../../hooks/useTSLots";

type AUnitOption = UnitOption<{
  type_id: string;
  conversion: number;
}>;

type AItemOption = ItemOption<{
  num: string;
  type_id: string;
  expires: boolean;
}>;

type AEmployeeOption = EmployeeOption<{}>;

type APalletOption = LotOption<{ pallet_name: string }>;

type ARecordOption = TypedSelectOption<
  "record",
  {
    vendor_id: string | null;
    vendor_name: string | null;
  }
>;

type ALocationOption = TypedSelectOption<"location", {}>;

type AInventoryStatusOption = TypedSelectOption<"status", {}>;

interface InvMovData {
  location: ALocationOption | null;
  status: AInventoryStatusOption | null;
  quantity: number;
}

interface InvLotFormData {
  lot_number: string | null;
  alternate_identifier: string | null;

  initial_revision: string | null;
  received_date: string;
  quantity_received: number;
  received_unit: AUnitOption | null;
  received_by: AEmployeeOption | null;

  is_released: boolean;
  released_revision: string | null;
  released_date: string | null;
  quantity_released: number;
  released_by: AEmployeeOption | null;

  item: AItemOption | null;

  associated_record: ARecordOption | null;

  box_folder_id: string | null;
  receiving_form_id: string | null;

  supplier_lot_batch_code: string | null;
  expiration_date: string | null;

  unit_cost: number | null;

  notes: string | null;

  use_quality_fields: boolean;
  right_first_time: boolean | null;
  rework: boolean | null;
  planned_deviation: boolean | null;

  inventory_movements: InvMovData[];
}

const formDefaults: InvLotFormData = {
  lot_number: null,
  alternate_identifier: null,

  initial_revision: null,
  received_date: new Date().toISOString(),
  quantity_received: 0,
  received_unit: null,
  received_by: null,

  is_released: false,
  released_revision: null,
  released_date: null,
  quantity_released: 0,
  released_by: null,

  item: null,

  associated_record: null,

  box_folder_id: null,
  receiving_form_id: null,

  supplier_lot_batch_code: null,
  expiration_date: null,

  unit_cost: null,

  notes: null,

  use_quality_fields: false,
  right_first_time: null,
  rework: null,
  planned_deviation: null,

  inventory_movements: [],
};

const validationSchema = yup.object({
  lot_number: yup.string().nullable(),
  alternate_identifier: yup.string().nullable(),

  initial_revision: yup
    .string()
    .required("New Lots require an Initial Revision"),
  received_date: yup.date().required("New Lots require a Received Date"),
  quantity_received: yup
    .number()
    .required("New Lots require the Initial Quantity")
    .moreThan(0, "Initial Quantity must be more than 0"),
  received_unit: yup
    .object({
      label: yup.string().required(),
      type: yup.string().is(["unit"]).required(),
      value: yup
        .object({
          id: yup.string().required(),
        })
        .required(),
    })
    .required("New Lots require an Unit of Measure"),
  received_by: yup
    .object({
      label: yup.string().required(),
      type: yup.string().is(["employee"]).required(),
      value: yup
        .object({
          id: yup.string().required(),
        })
        .required(),
    })
    .nullable(),
  is_released: yup.boolean().required(),
  released_revision: yup.string().when("is_released", {
    is: (is_released: boolean) => is_released === true,
    then: (schema) =>
      schema.required("Released Revision is required if lot is released."),
    otherwise: (schema) => schema.nullable(),
  }),
  released_date: yup.date().when("is_released", {
    is: (is_released: boolean) => is_released === true,
    then: (schema) =>
      schema.required("Released Date is required if lot is released."),
    otherwise: (schema) => schema.nullable(),
  }),
  quantity_released: yup.number().when("is_released", {
    is: (is_released: boolean) => is_released === true,
    then: (schema) =>
      schema
        .required("Released Quantity is required if lot is released.")
        .moreThan(
          0,
          "Released Quantity must be more than 0 if lot is released.",
        ),
    otherwise: (schema) =>
      schema.is([0], "Released Quantity must be 0 for unreleased lots"),
  }),
  released_by: yup
    .object({
      label: yup.string().required(),
      type: yup.string().is(["employee"]).required(),
      value: yup
        .object({
          id: yup.string().required(),
        })
        .required(),
    })
    .when("is_released", {
      is: (is_released: boolean) => is_released === true,
      then: (schema) =>
        schema.required("Released By is required if lot is released."),
      otherwise: (schema) => schema.nullable(),
    }),
  item: yup
    .object({
      label: yup.string().required(),
      type: yup.string().is(["item"]).required(),
      value: yup
        .object({
          id: yup.string().required(),
          expires: yup.boolean().required(),
        })
        .required(),
    })
    .required("New Lots require an Item"),
  associated_record: yup
    .object({
      label: yup.string().required(),
      type: yup.string().is(["record"]).required(),
      value: yup
        .object({
          id: yup.string().required(),
        })
        .required(),
    })
    .nullable(),
  box_folder_id: yup.string().nullable(),
  receiving_form_id: yup.string().nullable(),
  supplier_lot_batch_code: yup.string().nullable(),
  expiration_date: yup.date().when("item", {
    is: (item: any) => item?.value?.expires === true,
    then: (schema) =>
      schema.required("Expiration Date is required when items expires"),
    otherwise: (schema) => schema.nullable(),
  }),
  unit_cost: yup.number().required(),
  notes: yup.string().nullable(),
  use_quality_fields: yup.boolean().required(),
  right_first_time: yup.boolean().when("use_quality_fields", {
    is: (use_quality_fields: boolean) => use_quality_fields === true,
    then: (schema) =>
      schema.required(
        "Right First Time is required if lot uses quality fields",
      ),
    otherwise: (schema) => schema.nullable(),
  }),
  rework: yup.boolean().when("use_quality_fields", {
    is: (use_quality_fields: boolean) => use_quality_fields === true,
    then: (schema) =>
      schema.required("Rework is required if lot uses quality fields"),
    otherwise: (schema) => schema.nullable(),
  }),
  planned_deviation: yup.boolean().when("use_quality_fields", {
    is: (use_quality_fields: boolean) => use_quality_fields === true,
    then: (schema) =>
      schema.required(
        "Planned Deviation is required if lot uses quality fields",
      ),
    otherwise: (schema) => schema.nullable(),
  }),
  inventory_movements: yup.array().of(
    yup.object({
      location: yup
        .object({
          label: yup.string().required(),
          type: yup.string().is(["location"]).required(),
          value: yup
            .object({
              id: yup.string().required(),
            })
            .required(),
        })
        .required(),
      status: yup
        .object({
          label: yup.string().required(),
          type: yup.string().is(["status"]).required(),
          value: yup
            .object({
              id: yup.string().required(),
            })
            .required(),
        })
        .required(),
      quantity: yup.number().moreThan(0).required(),
    }),
  ),
});

export const PageInvLotForm: React.FC = () => {
  const navigate = useNavigate();
  const authContext = useAuth();

  // Mode detection from URL query parameters
  const [searchParams] = useSearchParams();
  const recId = searchParams.get("id");
  const refRecId = useRef(recId);
  const formMode = searchParams.get("mode") || "create";
  const refFormMode = useRef(formMode);

  // Form setup
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue,
    getValues,
  } = useForm<InvLotFormData>({
    resolver: yupResolver(validationSchema) as any,
    defaultValues: formDefaults,
  });

  useEffect(() => {
    refRecId.current = recId;
    refFormMode.current = formMode;
  }, [formMode, recId]);

  const {
    data: thisLotData,
    isLoading: thisLotLoading,
    error: thisLotError,
  } = useTSQLot(recId);

  const {
    data: itemsResponse,
    isLoading: itemsLoading,
    error: itemsError,
  } = useTSQItemList({
    filters: [[{ field: "track_lots", operator: "==", value: true }]],
  });
  const [itemOpts, palletItemId] = useMemo(() => {
    let opts: AItemOption[] = [];
    let id: string | null = null;
    if (itemsResponse?.items) {
      opts = itemsResponse.items.map((i) => ({
        label: `${i.number} - ${i.name}`,
        type: "item",
        value: {
          id: i.id,
          num: i.number,
          type_id: i.uom_type.id,
          expires: i.item_expires,
        },
      }));
      id = opts.find((o) => o.label.includes("MSP-1782"))!.value.id;
    }
    return [opts, id];
  }, [itemsResponse]);

  const {
    data: locsResponse,
    isLoading: locsLoading,
    error: locsError,
  } = useTSQLocationList(
    {
      filters: [
        [
          { field: "top_location_name", operator: "==", value: "Omaha" },
          { field: "name", operator: "!=", value: "Omaha" },
        ],
      ],
    },
    formMode !== "view",
  );
  const locOpts: ALocationOption[] = useMemo(() => {
    if (locsResponse?.items) {
      return locsResponse.items.map((l) => ({
        label: l.name,
        type: "location",
        value: {
          id: l.id,
        },
      }));
    } else {
      return [];
    }
  }, [locsResponse]);

  const {
    data: statusResponse,
    isLoading: statusLoading,
    error: statusError,
  } = useTSQInvStatusList(
    {
      filters: [[]],
    },
    formMode !== "view",
  );
  const statOpts: AInventoryStatusOption[] = useMemo(() => {
    if (statusResponse?.items) {
      return statusResponse.items.map((s) => ({
        label: s.name,
        type: "status",
        value: {
          id: s.id,
        },
      }));
    } else {
      return [];
    }
  }, [statusResponse]);

  const {
    data: unitsResponse,
    isLoading: unitsLoading,
    error: unitsError,
  } = useTSQUOMUnitList({
    filters: [[]],
  });
  const uomOpts: AUnitOption[] = useMemo(() => {
    if (unitsResponse?.items) {
      return unitsResponse.items.map((u) => ({
        label: u.unit_symbol,
        type: "unit",
        value: {
          id: u.id,
          type_id: u.uom_type_id,
          conversion: parseFloat(u.conversion_factor),
        },
      }));
    } else {
      return [];
    }
  }, [unitsResponse]);

  const {
    data: woResponse,
    isLoading: woLoading,
    error: woError,
  } = useTSQWorkOrderListView(
    {
      page: 0,
      page_size: 999999,
      sort_by: "work_orders.number DESC",
      filter: null,
    },
    "lot_form",
  );
  const {
    data: poResponse,
    isLoading: poLoading,
    error: poError,
  } = useTSQPurchOrderList({
    filters: [[]],
  });
  const recOpts: ARecordOption[] = useMemo(() => {
    if (woResponse?.items && poResponse?.items) {
      console.log("PO Response", poResponse);
      console.log("WO Response", woResponse);
      let poOpts: ARecordOption[] = poResponse.items.map((po) => ({
        label: po.number,
        type: "record",
        group: "Purchase Order",
        value: {
          id: po.id,
          table: "purchase_orders",
          vendor_id: po.vendor.id,
          vendor_name: po.vendor.name,
        },
      }));
      let woOpts: ARecordOption[] = woResponse.items.map((wo) => ({
        label: wo.number,
        type: "record",
        group: "Work Order",
        value: {
          id: wo.id,
          table: "work_orders",
          vendor_id: null,
          vendor_name: null,
        },
      }));
      return [...poOpts, ...woOpts];
    } else {
      return [];
    }
  }, [woResponse, poResponse]);

  const {
    data: empResponse,
    isLoading: empLoading,
    error: empError,
  } = useTSQPeopleList(
    {
      filters: [[]],
    },
    "employee",
  );
  const empOpts: AEmployeeOption[] = useMemo(() => {
    if (empResponse?.items) {
      return empResponse.items.map((e) => ({
        label: `${e.first_name} ${e.last_name}`,
        type: "employee",
        value: {
          id: e.id,
        },
      }));
    } else {
      return [];
    }
  }, [empResponse]);

  const {
    data: palletResponse,
    isLoading: palletsLoading,
    error: palletError,
  } = useTSQLotList(
    {
      filters: [[{ field: "item_id", operator: "==", value: palletItemId }]],
    },
    palletItemId !== null,
  );
  const palletOpts: APalletOption[] = useMemo(() => {
    if (palletResponse?.items) {
      return palletResponse.items.map((p) => ({
        label: p.lot_number,
        type: "lot",
        value: {
          id: p.id,
          pallet_name: p.alternate_identifier || "UNKNOWN",
        },
      }));
    } else {
      return [];
    }
  }, [palletResponse]);

  const loadingData =
    thisLotLoading ||
    poLoading ||
    woLoading ||
    empLoading ||
    palletsLoading ||
    itemsLoading ||
    locsLoading ||
    statusLoading ||
    unitsLoading;

  const createLotMutation = useTSMCreateLot();
  const updateLotMutation = useTSMUpdateLot();

  // State
  const [error, setError] = useState<string | null>(null);
  const [overrideLotGen, setOverrideLotGet] = useState<boolean>(false);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "inventory_movements",
  });

  useEffect(() => {
    if (formMode !== "view") {
      return;
    }
    if (
      thisLotData &&
      uomOpts.length > 0 &&
      itemOpts.length > 0 &&
      recOpts.length > 0 &&
      empOpts.length > 0 &&
      palletOpts.length > 0
    ) {
    } else {
      console.log({
        "Lot Data": thisLotData,
        "Item Options": itemOpts,
        "Unit Options": uomOpts,
        "Record Options": recOpts,
        "Employee Options": empOpts,
        "Pallet Options": palletOpts,
      });
    }
  }, [thisLotData, itemOpts, uomOpts, recOpts, empOpts, palletOpts, formMode]);

  const switchToCreateMode = () => {
    navigate("/inventory/lots/form?mode=create");
  };

  const addMovementEntry = () => {
    append({
      location: null,
      status: null,
      quantity: 0,
    });
  };

  const removeMovementEntry = (index: number) => {
    remove(index);
  };

  const getTotalMovementQuantity = () => {
    const movements = watch("inventory_movements") || [];
    return movements.reduce(
      (sum: number, movement: any) => sum + (Number(movement.quantity) || 0),
      0,
    );
  };

  const getRemainingQuantity = () => {
    const totalReceived = Number(watch("quantity_received")) || 0;
    const totalMovements = getTotalMovementQuantity();
    return totalReceived - totalMovements;
  };

  // Form submission
  const onSubmit = async (data: InvLotFormData) => {
    try {
      setError(null);

      if (formMode === "create") {
        // const lotPayload: FullLotCreate = {
        //   lot_data: {
        //     lot_number: data.lot_number,
        //     initial_revision: data.revision,
        //     item_id: data.item_id,
        //     supplier_lot_batch_code: data.supplier_lot_batch_code,
        //     received_date: data.received_date,
        //     expiration_date: data.expiration_date,
        //     received_uom_unit_id: data.received_uom_unit_id,
        //     quantity_received: data.quantity_received,
        //     unit_cost: data.unit_cost,
        //     associated_id: data.associated_id,
        //     associated_table: data.associated_table,
        //     notes: data.notes,
        //     description: data.description,
        //   },
        //   mov_data: data.mov_data,
        // };
        // const new_lot = await createLotMutation.mutateAsync(lotPayload);
        // navigate(`/inventory/lots/form?mode=view&id=${new_lot.id}`);
      }
    } catch (err: any) {
      console.error("Failed to save lot:", err);
      setError(
        err.response?.data?.detail || err.message || "Failed to save lot",
      );
    }
  };

  const handleCancel = () => {
    navigate("/inventory/lots");
  };

  if (formMode === "view" && loadingData) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Stack direction="column" gap={1}>
          <Typography variant="h4" component="h1">
            {formMode === "create"
              ? "Create Lot"
              : `View Lot: ${thisLotData?.lot_number || ""}`}
          </Typography>
          <Box display="flex" alignItems="center" gap={2} sx={{ mb: 3 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleCancel}
              variant="outlined"
            >
              Back to Lot List
            </Button>
            {formMode === "view" && (
              <Button
                variant="outlined"
                onClick={switchToCreateMode}
                startIcon={<AddIcon />}
              >
                Create New
              </Button>
            )}
            {/* {formMode === "view" && !loadingData && (
              <Button
                variant="outlined"
                onClick={handleUpdateDialogOpen}
                startIcon={<EditIcon />}
              >
                Update Lot
              </Button>
            )} */}
            {authContext.user?.is_superuser && (
              <Button
                variant="outlined"
                onClick={() => {
                  console.log(getValues());
                }}
              >
                DEBUG PRINT
              </Button>
            )}
          </Box>
        </Stack>

        {/* Error Alert */}
        {(error || thisLotError || itemsError || locsError || statusError) && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error ||
              thisLotError?.message ||
              itemsError?.message ||
              locsError?.message ||
              statusError?.message ||
              "An unknown error occurred"}
          </Alert>
        )}

        {/* Form */}
        {formMode === "view" ? (
          <Paper sx={{ p: 3 }}>
            <Stack direction="column" spacing={3}>
              <Box display="flex" gap={2}>
                <Stack direction="column" gap={2}>
                  <DisplayField
                    label="Lot Number:"
                    value={thisLotData?.lot_number || "UNKNOWN"}
                  />
                  <DisplayField
                    label="Initial Revision:"
                    value={thisLotData?.initial_revision || "UNKNOWN"}
                  />
                  <DisplayField
                    label="Received/Built Date:"
                    value={
                      thisLotData
                        ? new Date(
                            thisLotData.received_date,
                          ).toLocaleDateString("en-US")
                        : "UNKNOWN"
                    }
                  />
                  <DisplayField
                    label="Received By:"
                    value={
                      thisLotData
                        ? `${thisLotData.received_by?.first_name} ${thisLotData.received_by?.last_name}`
                        : "UNKNOWN"
                    }
                  />
                  <DisplayField
                    label="Received Quantity:"
                    value={
                      thisLotData
                        ? `${thisLotData.quantity_received}x ${thisLotData.received_uom_unit.unit_symbol}`
                        : "UNKNOWN"
                    }
                  />
                  <DisplayField
                    label="Supplier Batch Code:"
                    value={thisLotData?.supplier_lot_batch_code || "N/A"}
                  />
                </Stack>
                <Stack direction="column" gap={2}>
                  <DisplayField
                    label="Item:"
                    value={
                      thisLotData
                        ? `${thisLotData.item.number}\n${thisLotData.item.name}`
                        : "UNKNOWN"
                    }
                  />
                  <DisplayField
                    label="Released Revision:"
                    value={thisLotData?.released_revision || "N/A"}
                  />
                  <DisplayField
                    label="Released Date:"
                    value={
                      thisLotData
                        ? thisLotData.released_date
                          ? new Date(
                              thisLotData.released_date,
                            ).toLocaleDateString("en-US")
                          : "N/A"
                        : "N/A"
                    }
                  />
                  <DisplayField
                    label="Released By:"
                    value={
                      thisLotData?.released_by
                        ? `${thisLotData.released_by?.first_name} ${thisLotData.released_by?.last_name}`
                        : "N/A"
                    }
                  />
                  <DisplayField
                    label="Released Quantity:"
                    value={
                      thisLotData?.released_date
                        ? `${thisLotData.quantity_released}x ${thisLotData.received_uom_unit.unit_symbol}`
                        : "N/A"
                    }
                  />
                  <DisplayField
                    label="Expiration Date:"
                    value={
                      thisLotData
                        ? thisLotData.expiration_date
                          ? new Date(
                              thisLotData.expiration_date,
                            ).toLocaleDateString("en-US")
                          : "N/A"
                        : "N/A"
                    }
                  />
                </Stack>
              </Box>
              {thisLotData &&
                thisLotData.right_first_time !== null &&
                thisLotData.rework !== null &&
                thisLotData.planned_deviation !== null && (
                  <Box display="flex" gap={2}>
                    <Typography variant="body1">{`Right First Time? ${
                      thisLotData.right_first_time ? "Yes" : "No"
                    }`}</Typography>
                    <Typography variant="body1">{`Rework? ${
                      thisLotData.rework ? "Yes" : "No"
                    }`}</Typography>
                    <Typography variant="body1">{`Planned Deviation? ${
                      thisLotData.planned_deviation ? "Yes" : "No"
                    }`}</Typography>
                  </Box>
                )}
            </Stack>
          </Paper>
        ) : (
          <Paper sx={{ p: 3 }}>
            <form onSubmit={handleSubmit(onSubmit, console.error)}>
              <Stack spacing={3}>
                {/* Basic Information */}
                <Alert severity="error">
                  Manual Lot Creation is Temporarily Disabled
                </Alert>
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>

                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box sx={{ display: "column", gap: 2 }}>
                    <Controller
                      name="lot_number"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Lot Number *"
                          fullWidth
                          disabled={formMode === "view" || !overrideLotGen}
                          error={!!errors.lot_number}
                          helperText={
                            errors.lot_number?.message ||
                            "Auto-generated lot number"
                          }
                        />
                      )}
                    />
                    {!(formMode === "view") && (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={overrideLotGen}
                            onChange={() => {
                              setOverrideLotGet(!overrideLotGen);
                            }}
                          />
                        }
                        label={
                          formMode === "create"
                            ? "Override Lot Generation"
                            : "Update Lot Number"
                        }
                      />
                    )}
                  </Box>

                  <Controller
                    name="item"
                    control={control}
                    render={({ field: { value, onChange, ...restField } }) => (
                      <Autocomplete
                        {...restField}
                        value={value}
                        onChange={(e, v) => {
                          onChange(e);
                        }}
                        options={itemOpts}
                        getOptionLabel={(op) => op?.label || ""}
                        isOptionEqualToValue={(op, val) =>
                          op.value.id === val.value.id
                        }
                        renderInput={(params) => (
                          <TextField {...params} label="Item" />
                        )}
                      />
                    )}
                  />

                  <Controller
                    name="received_unit"
                    control={control}
                    render={({ field: { value, onChange, ...restField } }) => (
                      <Autocomplete
                        {...restField}
                        value={value}
                        onChange={(e, v) => {
                          onChange(e);
                        }}
                        options={uomOpts}
                        getOptionLabel={(op) => op?.label || ""}
                        isOptionEqualToValue={(op, val) =>
                          op.value.id === val.value.id
                        }
                        renderInput={(params) => (
                          <TextField {...params} label="Received Unit" />
                        )}
                      />
                    )}
                  />
                </Box>

                <Controller
                  name="initial_revision"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Initial Revision"
                      fullWidth
                      error={!!errors.initial_revision}
                    />
                  )}
                />

                <Controller
                  name="supplier_lot_batch_code"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Supplier Lot/Batch Code"
                      fullWidth
                      error={!!errors.supplier_lot_batch_code}
                      helperText={
                        errors.supplier_lot_batch_code?.message ||
                        "Optional supplier tracking code"
                      }
                    />
                  )}
                />

                {/* Item Details (if selected) */}
                {/* {selectedItem && (
                  <Box sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      gutterBottom
                    >
                      Selected Item Details
                    </Typography>
                    <Box sx={{ display: "flex", gap: 4 }}>
                      <Typography variant="body2">
                        <strong>Item Type:</strong>{" "}
                        {selectedItem.item_type || "N/A"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>UOM Type:</strong>{" "}
                        {selectedItem.uom_type.type_name}
                      </Typography>
                    </Box>
                  </Box>
                )} */}
                <Divider />

                {/* Dates */}
                <Typography variant="h6" gutterBottom>
                  Dates
                </Typography>

                <Box sx={{ display: "flex", gap: 2 }}>
                  <Controller
                    name="received_date"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        label="Received Date *"
                        value={field.value ? new Date(field.value) : null}
                        onChange={(date) => field.onChange(date?.toISOString())}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!errors.received_date,
                            helperText: errors.received_date?.message,
                          },
                        }}
                      />
                    )}
                  />

                  <Controller
                    name="expiration_date"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        label="Expiration Date"
                        value={field.value ? new Date(field.value) : null}
                        onChange={(date) =>
                          field.onChange(date?.toISOString() || "")
                        }
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!errors.expiration_date,
                            helperText:
                              errors.expiration_date?.message ||
                              "Optional expiration date",
                          },
                        }}
                      />
                    )}
                  />
                </Box>

                <Divider />

                {/* Quantities and Cost */}
                <Typography variant="h6" gutterBottom>
                  Quantities and Cost
                </Typography>

                <Box sx={{ display: "flex", gap: 2 }}>
                  <Controller
                    name="quantity_received"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Quantity Received *"
                        type="number"
                        fullWidth
                        error={!!errors.quantity_received}
                        helperText={errors.quantity_received?.message}
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    )}
                  />

                  <Controller
                    name="unit_cost"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Unit Cost *"
                        type="number"
                        fullWidth
                        error={!!errors.unit_cost}
                        helperText={errors.unit_cost?.message}
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{
                          startAdornment: (
                            <Typography sx={{ mr: 1 }}>$</Typography>
                          ),
                        }}
                      />
                    )}
                  />
                </Box>

                {/* Quantity Summary (Edit/View mode) */}

                <Divider />

                {/* Notes */}
                <Typography variant="h6" gutterBottom>
                  Additional Information
                </Typography>

                <Controller
                  name="notes"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Notes"
                      multiline
                      rows={4}
                      fullWidth
                      error={!!errors.notes}
                      helperText={
                        errors.notes?.message || "Optional notes about this lot"
                      }
                    />
                  )}
                />

                {/* {isCreateMode && (
                  <>
                    <Divider />
                    <Typography variant="h6" gutterBottom>
                      Inventory Placement
                    </Typography>

                    <Alert severity="info" sx={{ mb: 2 }}>
                      Specify where this inventory should be placed if inventory
                      exists at time of lot creation. Leave empty if lot has no
                      actual inventory. Total quantity must not exceed the
                      quantity received ({watch("quantity_received") || 0}).
                    </Alert>

                    {errors.mov_data && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {errors.mov_data.message}
                      </Alert>
                    )}

                    <TableContainer component={Paper} sx={{ mb: 2 }}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Location *</TableCell>
                            <TableCell>Status *</TableCell>
                            <TableCell>Quantity *</TableCell>
                            <TableCell>Notes</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {fields.map((field, index) => (
                            <TableRow key={field.id}>
                              <TableCell>
                                <Controller
                                  name={`mov_data.${index}.to_location_id`}
                                  control={control}
                                  render={({ field }) => (
                                    <FormControl
                                      fullWidth
                                      size="small"
                                      error={
                                        !!errors.mov_data?.[index]
                                          ?.to_location_id
                                      }
                                    >
                                      <InputLabel>Location</InputLabel>
                                      <Select {...field} label="Location">
                                        {locations.map((location) => (
                                          <MenuItem
                                            key={location.id}
                                            value={location.id}
                                          >
                                            {location.name} (
                                            {location.location_type})
                                          </MenuItem>
                                        ))}
                                      </Select>
                                      {errors.mov_data?.[index]
                                        ?.to_location_id && (
                                        <FormHelperText>
                                          {
                                            errors.mov_data[index]
                                              ?.to_location_id?.message
                                          }
                                        </FormHelperText>
                                      )}
                                    </FormControl>
                                  )}
                                />
                              </TableCell>
                              <TableCell>
                                <Controller
                                  name={`mov_data.${index}.to_status_id`}
                                  control={control}
                                  render={({ field }) => (
                                    <FormControl
                                      fullWidth
                                      size="small"
                                      error={
                                        !!errors.mov_data?.[index]?.to_status_id
                                      }
                                    >
                                      <InputLabel>Status</InputLabel>
                                      <Select {...field} label="Status">
                                        {statuses.map((status) => (
                                          <MenuItem
                                            key={status.id}
                                            value={status.id}
                                          >
                                            {status.name}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                      {errors.mov_data?.[index]
                                        ?.to_status_id && (
                                        <FormHelperText>
                                          {
                                            errors.mov_data[index]?.to_status_id
                                              ?.message
                                          }
                                        </FormHelperText>
                                      )}
                                    </FormControl>
                                  )}
                                />
                              </TableCell>
                              <TableCell>
                                <Controller
                                  name={`mov_data.${index}.quantity`}
                                  control={control}
                                  render={({ field }) => (
                                    <TextField
                                      {...field}
                                      type="number"
                                      size="small"
                                      error={
                                        !!errors.mov_data?.[index]?.quantity
                                      }
                                      helperText={
                                        errors.mov_data?.[index]?.quantity
                                          ?.message
                                      }
                                      sx={{ width: 120 }}
                                      inputProps={{
                                        step: 0.01,
                                        min: 0,
                                      }}
                                    />
                                  )}
                                />
                              </TableCell>
                              <TableCell>
                                <Controller
                                  name={`mov_data.${index}.notes`}
                                  control={control}
                                  render={({ field }) => (
                                    <TextField
                                      {...field}
                                      size="small"
                                      placeholder="Optional notes"
                                      sx={{ width: 150 }}
                                    />
                                  )}
                                />
                              </TableCell>
                              <TableCell>
                                <Tooltip title="Remove Entry">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => removeMovementEntry(index)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={addMovementEntry}
                        disabled={getRemainingQuantity() <= 0}
                      >
                        Add Location
                      </Button>

                      <Box sx={{ textAlign: "right" }}>
                        <Typography variant="body2" color="text.secondary">
                          Total Allocated: {getTotalMovementQuantity()}
                        </Typography>
                        <Typography
                          variant="body2"
                          color={
                            getRemainingQuantity() >= 0
                              ? "success.main"
                              : "warning.main"
                          }
                          fontWeight="medium"
                        >
                          Un-Allocated: {getRemainingQuantity()}
                        </Typography>
                      </Box>
                    </Box>

                    {getRemainingQuantity() < 0 && (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        Total allocated quantity ({getTotalMovementQuantity()})
                        must not exceed quantity received (
                        {watch("quantity_received") || 0}).
                      </Alert>
                    )}
                  </>
                )} */}

                {/* Form Actions */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "flex-end",
                    pt: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                    startIcon={<CancelIcon />}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={true}
                  >
                    {isSubmitting ? "Saving..." : "Create Lot"}
                  </Button>
                </Box>
              </Stack>
            </form>
          </Paper>
        )}
      </Box>
    </LocalizationProvider>
  );
};
